import { useCallback, useMemo } from 'react'
import {
  collectLabels,
  useFilteredTableState,
} from '@/components/admin/shared/hooks/use-filtered-table-state'
import type { SDPersistedFilterState } from './use-sd-session-storage'
import { DEFAULT_SD_FILTER_STATE, sdTableStore } from './use-sd-session-storage'
import type { SystemDesignProblem } from './use-system-design-problems'
import { SYSTEM_DESIGN_SEARCH_KEYS } from './use-system-design-search'

const PROBLEMS_PER_PAGE = 50

interface UseSDTableFiltersOptions {
  problems: SystemDesignProblem[]
  persist?: boolean
}

interface UseSDTableFiltersReturn {
  globalFilter: string
  setGlobalFilter: (value: string) => void
  difficultyFilter: string | null
  setDifficultyFilter: (value: string | null) => void
  sourceFilter: string | null
  setSourceFilter: (value: string | null) => void
  topicsFilter: string[]
  setTopicsFilter: (topics: string[]) => void
  filteredProblems: SystemDesignProblem[]
  allSources: string[]
  allTopics: string[]
  visibleCount: number
  loadMore: () => void
  globalFilterFn: (
    row: { getValue: (columnId: string) => unknown },
    columnId: string,
    filterValue: string,
  ) => boolean
}

/** A numeric query is an external id carried by one of the problem's sources. */
function matchesExactId(problem: SystemDesignProblem, id: string): boolean {
  return problem.allSources?.some((s) => s.externalId === id) ?? false
}

function applySDFilters(
  problems: SystemDesignProblem[],
  filters: SDPersistedFilterState,
): SystemDesignProblem[] {
  let filtered = problems

  if (filters.difficultyFilter) {
    filtered = filtered.filter(
      (p) => p.difficulty?.name === filters.difficultyFilter,
    )
  }

  if (filters.sourceFilter) {
    filtered = filtered.filter((p) =>
      p.allSources?.some((s) => s.source === filters.sourceFilter),
    )
  }

  if (filters.topicsFilter.length > 0) {
    filtered = filtered.filter((p) =>
      p.topics?.some((t) => filters.topicsFilter.includes(t)),
    )
  }

  return filtered
}

export function useSDTableFilters({
  problems,
  persist = false,
}: UseSDTableFiltersOptions): UseSDTableFiltersReturn {
  const {
    filters,
    setFilter,
    filteredRows,
    visibleCount,
    loadMore,
    globalFilterFn,
  } = useFilteredTableState<SystemDesignProblem, SDPersistedFilterState>({
    rows: problems,
    persist,
    store: sdTableStore,
    pageSize: PROBLEMS_PER_PAGE,
    defaultFilters: DEFAULT_SD_FILTER_STATE,
    searchKeys: SYSTEM_DESIGN_SEARCH_KEYS,
    matchesExactId,
    applyFilters: applySDFilters,
  })

  const setGlobalFilter = useCallback(
    (value: string) => setFilter('globalFilter', value),
    [setFilter],
  )
  const setDifficultyFilter = useCallback(
    (value: string | null) => setFilter('difficultyFilter', value),
    [setFilter],
  )
  const setSourceFilter = useCallback(
    (value: string | null) => setFilter('sourceFilter', value),
    [setFilter],
  )
  const setTopicsFilter = useCallback(
    (topics: string[]) => setFilter('topicsFilter', topics),
    [setFilter],
  )

  const allSources = useMemo(
    () =>
      collectLabels(problems, (p) => (p.allSources ?? []).map((s) => s.source)),
    [problems],
  )

  const allTopics = useMemo(
    () => collectLabels(problems, (p) => p.topics ?? []),
    [problems],
  )

  return {
    globalFilter: filters.globalFilter,
    setGlobalFilter,
    difficultyFilter: filters.difficultyFilter,
    setDifficultyFilter,
    sourceFilter: filters.sourceFilter,
    setSourceFilter,
    topicsFilter: filters.topicsFilter,
    setTopicsFilter,
    filteredProblems: filteredRows,
    allSources,
    allTopics,
    visibleCount,
    loadMore,
    globalFilterFn,
  }
}
